import React, { useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { ReactComponent as BaseInfoIcon } from '@ubeya/shared-web/assets/info.svg';
import {
  Modal as BaseModal,
  FlexColumn,
  Header2SemiBold,
  TextArea as BaseTextArea
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useOSEmployee from '@ubeya/shared/hooks/employees/useOSEmployee';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import { EMPLOYEE_CLIENT_PREFERENCE_STATUS } from '@ubeya/shared-web/utils/employee';
import { uniq } from 'lodash';

const Modal = styled(BaseModal)`
  .content {
    display: flex;
    align-items: center;
  }
`;

const TextArea = styled(BaseTextArea)`
  margin-top: 16px;
  width: 100%;
  max-height: 320px;
`;

const InfoIcon = styled(BaseInfoIcon)`
  margin-bottom: 10px;
  g {
    circle {
      stroke: ${({ theme }) => theme.colors.primary};
    }
    path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const Content = styled(FlexColumn)`
  gap: 8px;
  padding-block: 32px;
  width: 80%;
  align-items: center;
  text-align: center;
`;

const Title = styled(Header2SemiBold)`
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
`;

// isClient - indicates if Client Account or Supplier Account
const BlockEmployeeOnRatingModal = ({ data, onClose, onEmployeeStatusChange, isClient = true }) => {
  const { t } = useContext(ConfigContext);
  const { account } = useAccount();
  const { updateEmployee: updateOSEmployee } = useOSEmployee({ employeeId: data?.employee?.id, enabled: false });
  const { updateEmployee, data: employeeData, isLoading } = useEmployee({
    employeeId: data?.employee?.id,
    enabled: !isClient
  });

  const [employeeBlockReason, setEmployeeBlockReason] = useState(null);

  const employeeName = `${data?.employee?.firstName} ${data?.employee?.lastName}`.trim();
  const clientName = isClient ? account.name : data?.clientBranch?.clientName ?? data?.clientName;

  const onBlock = useCallback(async () => {
    const comment = t('employeeBlockedFromClientComment', {
      clientName,
      comment: employeeBlockReason
    });

    if (!isClient) {
      const newBlockedClient = data.clientId ?? data?.clientBranch?.accountId;

      const blockedClients =
        employeeData?.employeeClients
          ?.filter((client) => client.status === EMPLOYEE_CLIENT_PREFERENCE_STATUS.BLOCKED)
          .map((client) => client.clientId) || [];

      const preferredClients =
        employeeData?.employeeClients
          ?.filter((client) => client.status === EMPLOYEE_CLIENT_PREFERENCE_STATUS.PREFERRED)
          .map((client) => client.clientId) || [];

      await updateEmployee({
        accountId: account.id,
        employeeId: data.employee.id,
        blockedClients: uniq([...blockedClients, newBlockedClient]),
        preferredClients: preferredClients.filter((client) => client !== newBlockedClient),
        blockingReason: comment
      });
    } else {
      await updateOSEmployee({
        accountId: account.id,
        employeeId: data.employee.id ?? data.employee.employeeId,
        blockingReason: comment,
        clientStatus: EMPLOYEE_CLIENT_PREFERENCE_STATUS.BLOCKED
      });
    }

    onEmployeeStatusChange?.();
    onClose();
  }, [
    account.id,
    clientName,
    data?.clientBranch?.accountId,
    data.clientId,
    data.employee.employeeId,
    data.employee.id,
    employeeBlockReason,
    isClient,
    onClose,
    onEmployeeStatusChange,
    t,
    employeeData,
    updateEmployee,
    updateOSEmployee
  ]);

  return (
    <>
      <Modal
        size="small"
        onConfirm={onBlock}
        onClose={onClose}
        isPaddingContent={false}
        showConfirmButton
        showCancelButton
        disabledConfirm={!employeeBlockReason}
        loadingConfirm={isLoading}
        confirmText={t('block')}
        usePortal>
        <Content>
          <InfoIcon />
          <Title>{t('employeeRatedOneStarModalHeader')}</Title>
          <p>{t('employeeRatedOneStarModalBody', { employeeName, clientName })}</p>

          <TextArea
            value={employeeBlockReason}
            onChange={setEmployeeBlockReason}
            placeholder={t('addComment')}
            minRows={3}
            maxLength={500}
          />
        </Content>
      </Modal>
    </>
  );
};

export default BlockEmployeeOnRatingModal;
